import React, { useState, useEffect, useMemo } from 'react'
import {
    Box,
    Grid,
    Typography,
    List,
    ListItemText,
    ListItemButton,
    Divider,
    Button,
    Chip,
    IconButton,
    Toolbar,
    Paper,
    CircularProgress,
    Tooltip
} from '@mui/material'
import {
    ArrowBackIos as ArrowBackIcon,
    RestoreFromTrash as RestoreIcon,
    Info as InfoIcon,
    History as HistoryIcon,
    Visibility as ViewIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useReportsContext, useUser } from '../../context'

const DeletedReportsPage = () => {
    const navigate = useNavigate()
    const { reportesSeleccionados, reportsToShow, restoreReport, totalItemsInDeleted, setIsDeletedView } = useReportsContext()
    const { admin, isSuperAdmin } = useUser()

    const [selectedReport, setSelectedReport] = useState(null)
    const [loading, setLoading] = useState(false)

    // Recuperar página de localStorage o por defecto 0
    const [page, setPage] = useState(() => {
        const savedPage = localStorage.getItem('deleted_reports_last_page')
        return savedPage ? parseInt(savedPage, 10) : 0
    })

    const pageSize = 10

    useEffect(() => {
        if (!admin && !isSuperAdmin) {
            navigate('/home')
            return
        }
        fetchDeletedReports()
        // Persistir la página actual
        localStorage.setItem('deleted_reports_last_page', page)
    }, [page])

    useEffect(() => {
        return () => {
            setIsDeletedView(false)
        }
    }, [setIsDeletedView])

    const fetchDeletedReports = async () => {
        setLoading(true)
        await reportesSeleccionados('todos', page * pageSize, pageSize, 'todos', 'updatedAt', -1, true)
        setLoading(false)
    }

    const handleRestore = async (id) => {
        if (window.confirm('¿Está seguro de que desea restaurar este reporte? Volverá a estar activo en el sistema.')) {
            await restoreReport(id)
            setSelectedReport(null)
            fetchDeletedReports()
        }
    }

    const viewExecution = (report) => {
        navigate(`/assignment/${report._id}`)
    }

    // Lógica para el selector de páginas (Sliding Window: 3 izq, actual, 3 der)
    const totalPages = Math.ceil(totalItemsInDeleted / pageSize)

    const paginationRange = useMemo(() => {
        const current = page + 1
        const delta = 3
        const left = current - delta
        const right = current + delta + 1
        const range = []
        const rangeWithDots = []
        let l

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= left && i < right)) {
                range.push(i)
            }
        }

        for (const i of range) {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1)
                } else if (i - l !== 1) {
                    rangeWithDots.push('...')
                }
            }
            rangeWithDots.push(i)
            l = i
        }

        return rangeWithDots
    }, [totalPages, page])

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', p: 2, bgcolor: '#f5f5f5' }}>
            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Toolbar sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                        Historial de Reportes Eliminados
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'gray', mr: 2 }}>
                        Total: {totalItemsInDeleted} registros
                    </Typography>
                </Toolbar>

                <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    {/* Lista Izquierda */}
                    <Grid item xs={12} md={4} sx={{ borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', bgcolor: '#fff' }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : (
                                <List sx={{ p: 0 }}>
                                    {reportsToShow.length === 0 ? (
                                        <Typography sx={{ p: 3, textAlign: 'center', color: 'gray' }}>
                                            No hay reportes eliminados en esta página.
                                        </Typography>
                                    ) : (
                                        reportsToShow.map((report) => (
                                            <React.Fragment key={report._id}>
                                                <ListItemButton
                                                    selected={selectedReport?._id === report._id}
                                                    onClick={() => setSelectedReport(report)}
                                                    sx={{
                                                        py: 2,
                                                        borderLeft: selectedReport?._id === report._id ? '4px solid #BB2D2D' : '4px solid transparent'
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                    OT: {report.newIdIndex || report.idIndex}
                                                                </Typography>
                                                                <Chip label={report.reportType} size="small" variant="outlined" />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 0.5 }}>
                                                                SAP ID: {report.sapId?.includes('_DELETED_') ? report.sapId.split('_DELETED_')[0] : report.sapId}
                                                                <br />
                                                                Eliminado: {new Date(report.updatedAt).toLocaleDateString()}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <Divider />
                                            </React.Fragment>
                                        ))
                                    )}
                                </List>
                            )}
                        </Box>

                        {/* Footer de Paginación Mejorado */}
                        <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', borderTop: '1px solid #eee', bgcolor: '#fafafa', gap: 0.5 }}>
                            <IconButton
                                size="small"
                                disabled={page === 0 || loading}
                                onClick={() => setPage(p => p - 1)}
                            >
                                <PrevIcon />
                            </IconButton>

                            {paginationRange.map((p, idx) => (
                                p === '...' ? (
                                    <Typography key={idx} variant="body2" sx={{ px: 1 }}>...</Typography>
                                ) : (
                                    <Button
                                        key={idx}
                                        size="small"
                                        variant={page === p - 1 ? 'contained' : 'text'}
                                        onClick={() => setPage(p - 1)}
                                        sx={{
                                            minWidth: 32,
                                            height: 32,
                                            p: 0,
                                            borderRadius: '50%',
                                            bgcolor: page === p - 1 ? '#BB2D2D' : 'transparent',
                                            color: page === p - 1 ? '#fff' : '#333',
                                            '&:hover': {
                                                bgcolor: page === p - 1 ? '#9a2424' : '#eee'
                                            }
                                        }}
                                    >
                                        {p}
                                    </Button>
                                )
                            ))}

                            <IconButton
                                size="small"
                                disabled={page >= totalPages - 1 || loading}
                                onClick={() => setPage(p => p + 1)}
                            >
                                <NextIcon />
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* Detalle Derecha */}
                    <Grid item xs={12} md={8} sx={{ height: '100%', overflowY: 'auto', bgcolor: '#fff' }}>
                        {selectedReport ? (
                            <Box sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                                            OT {selectedReport.newIdIndex || selectedReport.idIndex}
                                        </Typography>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            ID SAP: {selectedReport.sapId?.includes('_DELETED_') ? selectedReport.sapId.split('_DELETED_')[0] : selectedReport.sapId}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<RestoreIcon />}
                                        onClick={() => handleRestore(selectedReport._id)}
                                        sx={{ borderRadius: 10, px: 3, bgcolor: '#BB2D2D', color: '#fff', '&:hover': { bgcolor: '#9a2424' } }}
                                    >
                                        Restaurar Reporte
                                    </Button>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>Información del Sitio</Typography>
                                            <Typography variant="body1"><strong>ID Obra:</strong> {selectedReport.obra?.idObra || selectedReport.site}</Typography>
                                            <Typography variant="body1"><strong>Descripción:</strong> {selectedReport.obra?.descripcion || 'N/A'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>Máquina</Typography>
                                            <Typography variant="body1"><strong>Equipo:</strong> {selectedReport.maquina?.equ || selectedReport.machine}</Typography>
                                            <Typography variant="body1"><strong>Modelo:</strong> {selectedReport.maquina?.model} {selectedReport.maquina?.brand}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>Estado al eliminar</Typography>
                                            <Typography variant="body1"><strong>Estado:</strong> {selectedReport.state}</Typography>
                                            <Typography variant="body1"><strong>Progreso:</strong> {selectedReport.progress}%</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>Fechas</Typography>
                                            <Typography variant="body1"><strong>Programado:</strong> {new Date(selectedReport.datePrev).toLocaleDateString()}</Typography>
                                            <Typography variant="body1"><strong>Eliminado el:</strong> {new Date(selectedReport.updatedAt).toLocaleString()}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 4 }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffde7', border: '1px solid #fff59d' }}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InfoIcon fontSize="small" color="warning" />
                                            Este reporte está en la papelera. Use el botón superior si desea habilitarlo nuevamente.
                                        </Typography>
                                    </Paper>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ViewIcon />}
                                            onClick={() => viewExecution(selectedReport)}
                                            sx={{ borderRadius: 10, borderColor: '#BB2D2D', color: '#BB2D2D', '&:hover': { borderColor: '#9a2424', bgcolor: '#fdf2f2' } }}
                                        >
                                            Ver Ejecución
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'gray' }}>
                                <HistoryIcon sx={{ fontSize: 80, mb: 2, opacity: 0.1 }} />
                                <Typography variant="h6" sx={{ opacity: 0.5 }}>Seleccione un reporte para ver el detalle</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    )
}

export default DeletedReportsPage
